import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "@phosphor-icons/react";
import { Reveal } from "../components/Reveal";
import { Wordmark } from "../components/marketing/Wordmark";
import { LangToggle } from "../components/marketing/Header";
import { fmtDate } from "../components/marketing/Insights";
import { useLanguage } from "../context/LanguageContext";
import { EMAIL, PHONE_DISPLAY, WHATSAPP_URL } from "../i18n/content";

export const BlogFooter = () => {
  const { t } = useLanguage();
  return (
  <footer className="bg-brand-ink text-brand-paper">
    <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-12 flex flex-col md:flex-row gap-6 md:items-center justify-between">
      <p className="text-sm text-white/60 max-w-md leading-relaxed">{t.footer.tagline}</p>
      <div className="text-sm text-white/60 flex flex-col gap-1">
        <a href={`mailto:${EMAIL}`} className="hover:text-white transition-colors duration-300">{EMAIL}</a>
        <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors duration-300">{PHONE_DISPLAY}</a>
      </div>
    </div>
  </footer>
  );
};

export const BlogHeader = ({ backTo = "/", backLabel }) => {
  const { t } = useLanguage();
  return (
    <header className="sticky top-0 z-50 bg-brand-paper/85 backdrop-blur-xl border-b border-black/10">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" data-testid="blog-header-logo" className="flex items-center gap-3 focus:outline-none focus:ring-2 focus:ring-brand-green" aria-label="PT Darma Daya Guna — home">
          <Wordmark size="text-3xl" />
        </Link>
        <div className="flex items-center gap-4">
          <LangToggle />
          <Link
            data-testid="blog-back-link"
            to={backTo}
            className="hidden sm:flex items-center gap-2 text-sm font-semibold text-black/60 hover:text-black transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-brand-green"
          >
            <ArrowLeft size={15} weight="bold" />
            {backLabel || t.servicePage.back}
          </Link>
        </div>
      </div>
    </header>
  );
};

export default function Articles() {
  const { t, lang } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = `${t.blog.title} — PT Darma Daya Guna`;
    return () => {
      document.title = "PT Darma Daya Guna — Training & Consultant";
    };
  }, [t]);

  return (
    <div className="grain bg-brand-paper text-brand-ink font-satoshi min-h-screen" data-testid="articles-page">
      <BlogHeader />
      <main className="max-w-[1200px] mx-auto px-6 md:px-10 pt-14 md:pt-20 pb-20 md:pb-28">
        <Reveal>
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-black/45 mb-8">
            <Link to="/" className="hover:text-black transition-colors">DDG</Link>
            <span className="mx-2 text-brand-red">/</span>
            <span className="text-brand-green">{t.nav.articles}</span>
          </p>
          <h1 data-testid="articles-title" className="font-cabinet font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.02] max-w-3xl">
            {t.blog.title}
          </h1>
          <p className="mt-6 max-w-2xl text-base md:text-lg leading-relaxed text-black/60">{t.blog.desc}</p>
        </Reveal>

        <div className="mt-16 md:mt-24 flex flex-col border-t border-black/10">
          {t.articles.map((a, i) => (
            <Reveal key={a.slug} delay={0.06 * i}>
              <Link
                data-testid={`article-row-${a.slug}`}
                to={`/artikel/${a.slug}`}
                className="group grid md:grid-cols-12 gap-4 md:gap-8 items-start py-9 md:py-12 border-b border-black/10 px-2 md:px-4 transition-colors duration-300 hover:bg-black/[0.02] focus:outline-none focus:ring-2 focus:ring-brand-green"
              >
                <div className="md:col-span-3 flex md:flex-col gap-3 md:gap-2">
                  <span className="bg-brand-green/10 text-brand-green px-3 py-1 text-[10px] font-bold tracking-[0.15em] uppercase w-fit">
                    {a.category}
                  </span>
                  <span className="text-xs text-black/40">
                    {fmtDate(a.date, lang)} · {a.readTime} {t.insights.minRead}
                  </span>
                </div>
                <div className="md:col-span-8">
                  <h2 className="font-cabinet font-bold text-xl sm:text-2xl lg:text-3xl tracking-tight leading-snug group-hover:text-brand-green transition-colors duration-300">
                    {a.title}
                  </h2>
                  <p className="mt-3 text-sm md:text-base leading-relaxed text-black/55 max-w-2xl">{a.excerpt}</p>
                </div>
                <div className="hidden md:flex md:col-span-1 justify-end pt-1">
                  <ArrowUpRight size={24} weight="bold" className="text-black/25 transition-[color,transform] duration-300 group-hover:text-brand-red group-hover:translate-x-1 group-hover:-translate-y-1" />
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </main>
      <BlogFooter />
    </div>
  );
}
